import type { i18n as I18nInstance } from "i18next";
import { translationResources } from "./resources";
import { replaceWholePhrase } from "./phraseReplacement";

type SupportedLocale = keyof typeof translationResources;

const DEFAULT_LOCALE: SupportedLocale = "en";
const TRANSLATABLE_ATTRIBUTES = ["placeholder", "title", "aria-label"] as const;
const SKIP_TRANSLATION_SELECTOR = "[data-no-auto-translate], [translate='no'], .notranslate";

const LIVE_TRANSLATE_MIN_WORDS = (() => {
  const configured = (import.meta.env.VITE_I18N_AUTO_TRANSLATE_MIN_WORDS as string | undefined)?.trim();
  const parsed = Number.parseInt(configured ?? "2", 10);
  if (!Number.isFinite(parsed) || parsed < 1) return 2;
  return parsed;
})();

const NAME_LIKE_PATTERN = /^[\p{Lu}][\p{L}'-]+(?:\s+[\p{Lu}][\p{L}'-]+){1,3}$/u;

const MANUAL_PHRASE_OVERRIDES: Partial<Record<SupportedLocale, Record<string, string>>> = {
  de: {
    Polish: "Polnisch",
    English: "Englisch",
    German: "Deutsch",
    French: "Französisch",
    "Search anything...": "Alles durchsuchen...",
    Housekeeping: "Housekeeping",
    "Room Calendar": "Zimmerkalender",
    "Room Type Availability": "Verfügbare Zimmertypen",
    Availability: "Verfügbarkeit",
    "Room Availability": "Zimmerverfügbarkeit",
    "Rate Plan": "Tarifplan",
    "Check-in": "Check-in",
    "Check-out": "Check-out",
    "Night Audit": "Night Audit",
    "Rates & Inventory": "Preise & Verfügbarkeit",

    // Billing dashboard
    "Billing Dashboard": "Abrechnungsübersicht",
    "Open Folios": "Offene Folios",
    "Unpaid Balances": "Offene Salden",
    "Today's Check-outs": "Heutige Abreisen",
    "All settled": "Alle abgerechnet",
    "Paid Invoices": "Bezahlte Rechnungen",
    collected: "eingezogen",
    "Pending Invoices": "Ausstehende Rechnungen",
    "Cancelled / Corrected": "Storniert / korrigiert",
    "Refunds": "Rückerstattungen",
    "Fiscal Documents": "Fiskaldokumente",
    fiscalized: "fiskalisiert",
    pending: "ausstehend",
    "Cash / Card / Bank Transfer Totals": "Barzahlung / Karte / Banküberweisung – Gesamt",
    "No payments recorded for this period.": "Für diesen Zeitraum wurden keine Zahlungen erfasst.",
    "Total collected": "Insgesamt eingezogen",
    "Revenue by Department": "Umsatz nach Abteilung",
    "No revenue data for this period.": "Für diesen Zeitraum liegen keine Umsatzdaten vor.",
    "VAT Summary": "MwSt.-Übersicht",
    estimated: "geschätzt",
    "No revenue data for VAT estimation.": "Keine Umsatzdaten für die Mehrwertsteuerschätzung.",
    Folios: "Folios",
    Invoices: "Rechnungen",
    Payments: "Zahlungen",
    Fiscal: "Fiskal",
    Splits: "Aufteilungen",
    Stay: "Aufenthalt",
    Charges: "Leistungen",
    Balance: "Saldo",

    // Front Office
    "Front Office": "Rezeption",
    "Welcome back": "Willkommen zurück",
    "Today at the desk": "Heute an der Rezeption",
    Arrivals: "Anreisen",
    Departures: "Abreisen",
    "Ready Rooms": "Bereite Zimmer",
    "Pending Payments": "Ausstehende Zahlungen",
    "Open Requests": "Offene Anfragen",
    "Room & Desk Status": "Zimmer- und Rezeptionsstatus",
    "Assigned Rooms": "Zugewiesene Zimmer",
    "Room Ready": "Zimmer bereit",
    "Checked In": "Eingecheckt",
    "Key Candidates": "Schlüsselkandidaten",
    "Room Moves": "Zimmerwechsel",
    "Priority Desk Work": "Prioritäre Aufgaben",
    "Ready arrivals": "Bereite Anreisen",
    "Due departures": "Fällige Abreisen",
    "No checked-in departures are waiting.": "Keine eingecheckten Abreisen warten.",
    "Keys to issue": "Auszugebende Schlüssel",
    "Follow-ups": "Nachverfolgungen",
    "Create Key": "Schlüssel erstellen",
    "Guest Queue": "Gästewarteschlange",
    Now: "Jetzt",
    Other: "Andere",
    "Needs Attention": "Benötigt Aufmerksamkeit",
    Departed: "Abgereist",
    Arriving: "Anreisend",
    "Next Action": "Nächste Aktion",
    "View Stay": "Aufenthalt anzeigen",
    "Check In": "Einchecken",
    "View all queue": "Gesamte Warteschlange anzeigen",
    "Find Guest": "Gast suchen",
    "Search Guest": "Gast suchen",
    "Walk-in": "Walk-in",
    "Post Charge": "Leistung buchen",
    "Room Move": "Zimmerwechsel",
    "Add Note": "Notiz hinzufügen",
    "Quick Search": "Schnellsuche",
    "Search guest or reservation...": "Gast oder Reservierung suchen...",
    "Room Number Lookup": "Zimmernummer suchen",
    "Handover Notes": "Übergabenotizen",
    "No open handover notes": "Keine offenen Übergabenotizen",
    "View all handover notes": "Alle Übergabenotizen anzeigen",

    // Housekeeping
    "Room Readiness Command Board": "Zimmerbereitschaft – Übersichtsboard",
    "Designed for large hotels: priority first, floors second, full inventory on demand":
      "Für große Hotels konzipiert: zuerst Prioritäten, dann Etagen, vollständiger Bestand auf Abruf",
    Priority: "Priorität",
    Floors: "Etagen",
    "All rooms": "Alle Zimmer",
    Inspected: "Geprüft",
    Clean: "Sauber",
    Dirty: "Schmutzig",
    Pickup: "Nachreinigung",
    Blocked: "Gesperrt",
    "Task Management": "Aufgabenverwaltung",
    "Create housekeeping tasks and work the open queue":
      "Housekeeping-Aufgaben erstellen und die offene Warteschlange bearbeiten",
    Room: "Zimmer",
    Task: "Aufgabe",
    Cleaning: "Reinigung",
    Inspection: "Inspektion",
    "Deep clean": "Grundreinigung",
    Turndown: "Abendservice",
    "Maintenance follow-up": "Technische Nachverfolgung",
    Low: "Niedrig",
    Medium: "Mittel",
    High: "Hoch",
    Critical: "Kritisch",
    "Assign to": "Zuweisen an",
    "Attendant name": "Name der zuständigen Person",
    Note: "Notiz",
    "Optional task note": "Optionale Aufgabennotiz",
    "Create Task": "Aufgabe erstellen",
    "Creating...": "Wird erstellt...",
    "Open tasks": "Offene Aufgaben",
    active: "aktiv",
    overdue: "überfällig",
    All: "Alle",
    Overdue: "Überfällig",
    Unassigned: "Nicht zugewiesen",
    "No open housekeeping tasks.": "Keine offenen Housekeeping-Aufgaben.",
    "out of order": "außer Betrieb",
    vacant: "frei",
    dirty: "schmutzig",

    // Room calendar and room-type availability
    "Arriving Today": "Heutige Anreisen",
    "Departing Today": "Heutige Abreisen",
    Today: "Heute",
    "7 Days": "7 Tage",
    "14 Days": "14 Tage",
    Month: "Monat",
    "New Reservation": "Neue Reservierung",
    "All Types": "Alle Zimmertypen",
    "All floors": "Alle Etagen",
    "Room no.": "Zimmernr.",
    "Unassigned Reservations": "Nicht zugewiesene Reservierungen",
    "None — reservations without a room will appear here to drag onto the grid.":
      "Keine – Reservierungen ohne Zimmer werden hier angezeigt und können in den Kalender gezogen werden.",
    "Sellable rooms left per room type": "Verfügbare Zimmer nach Zimmerkategorie",
    "Free today": "Heute frei",
    "Across all types": "Über alle Kategorien hinweg",
    "Low days": "Tage mit geringer Verfügbarkeit",
    "≤20% in range": "≤20 % im Zeitraum",
    "Sold-out days": "Ausverkaufte Tage",
    "0 free in range": "0 frei im Zeitraum",
    Overbooking: "Überbuchung",
    "Negative in range": "Negativ im Zeitraum",
    Th: "Do",
    Fr: "Fr",
    Sa: "Sa",
    Su: "So",
    Mo: "Mo",
    Tu: "Di",
    We: "Mi",

    // Night Audit
    "Audit Checklist": "Audit-Checkliste",
    "Exceptions Queue": "Ausnahmen-Warteschlange",
    "Open Balances Review": "Offene Salden prüfen",
    "Room & Tax Posting Preview": "Vorschau der Zimmer- und Steuerbuchungen",
    "No-show Handling": "No-Show-Bearbeitung",
    "Overstay/Understay Exceptions": "Ausnahmen bei Über- oder Unterschreitung der Aufenthaltsdauer",
    "Rate Integrity / Outliers": "Tarifintegrität und Ausreißer prüfen",
    "Housekeeping / Room Availability Summary": "Housekeeping-/Zimmerverfügbarkeitsübersicht",
    "Final Control": "Abschlusskontrolle",
    Resolve: "Erledigen",
    blocking: "blockierend",
    warning: "Warnung",
    info: "Info",
    resolved: "erledigt",
    "resolve in Front Office": "An der Rezeption beheben",
    "Blocking items muessen vor Close geloest werden.":
      "Blockierende Punkte müssen vor dem Tagesabschluss gelöst werden.",
    "Room Revenue": "Zimmerumsatz",
    Taxes: "Steuern",
    Packages: "Pakete",
    Charge: "Belasten",
    Waive: "Erlassen",
    "Rate outlier in DLX": "Preisabweichung in DLX",
    "No unresolved arrivals for this business date.": "Keine ungeklärten Anreisen für dieses Geschäftsdatum.",
    done: "erledigt",
  },
  pl: {
    Polish: "Polski",
    English: "Angielski",
    German: "Niemiecki",
    French: "Francuski",
    Housekeeping: "Housekeeping",
    "Room Calendar": "Kalendarz pokoi",
    "Room Type Availability": "Dostęne typy pokoi",
    Availability: "Dostępność",
    "Room Availability": "Dostępność pokoi",
    "Rate Plan": "Plan taryfowy",
    "Check-in": "Check-in",
    "Check-out": "Check-out",
    "Night Audit": "Night Audit",
    "Rates & Inventory": "Stawki i dostępność",
  },
  fr: {
    Polish: "Polonais",
    English: "Anglais",
    German: "Allemand",
    French: "Français",
    Housekeeping: "Housekeeping",
    "Room Calendar": "Calendrier des chambres",
    "Room Type Availability": "Types de chambres disponibles",
    Availability: "Disponibilité",
    "Room Availability": "Disponibilité des chambres",
    "Rate Plan": "Plan tarifaire",
    "Check-in": "Enregistrement",
    "Check-out": "Départ",
    "Night Audit": "Night Audit",
    "Rates & Inventory": "Tarifs et disponibilité",
  },
};

function resolveDomainTarget(locale: SupportedLocale, manualKey: string, fallback: string): string {
  return MANUAL_PHRASE_OVERRIDES[locale]?.[manualKey] ?? fallback;
}

const DOMAIN_TERM_REPLACEMENTS: Partial<Record<SupportedLocale, Array<readonly [string, string]>>> = {
  de: [
    ["departures not checked out", "nicht ausgecheckte Abreisen"],
    ["arrivals not checked in", "nicht eingecheckte Anreisen"],
    ["critical work orders", "kritische Arbeitsaufträge"],
    ["open parking sessions", "offene Parkvorgänge"],
    ["unpaid folios", "unbezahlte Folios"],
    ["overdue arrivals", "verspätete Anreisen"],
    ["overdue arrival", "verspätete Anreise"],
    ["room calendar", resolveDomainTarget("de", "Room Calendar", "Zimmerkalender")],
    ["room availability", resolveDomainTarget("de", "Room Availability", "Zimmerverfügbarkeit")],
    ["room type", resolveDomainTarget("de", "Room Type", "Zimmerkategorie")],
    ["availability", resolveDomainTarget("de", "Availability", "Verfügbarkeit")],
    ["rate plan", resolveDomainTarget("de", "Rate Plan", "Tarifplan")],
    ["housekeeping", resolveDomainTarget("de", "Housekeeping", "Housekeeping")],
  ],
  pl: [
    ["room calendar", resolveDomainTarget("pl", "Room Calendar", "Kalendarz pokoi")],
    ["room availability", resolveDomainTarget("pl", "Room Availability", "Dostępność pokoi")],
    ["room type", resolveDomainTarget("pl", "Room Type", "Typ pokoju")],
    ["availability", resolveDomainTarget("pl", "Availability", "Dostępność")],
    ["rate plan", resolveDomainTarget("pl", "Rate Plan", "Plan taryfowy")],
    ["housekeeping", resolveDomainTarget("pl", "Housekeeping", "Housekeeping")],
  ],
  fr: [
    ["room calendar", resolveDomainTarget("fr", "Room Calendar", "Calendrier des chambres")],
    ["room availability", resolveDomainTarget("fr", "Room Availability", "Disponibilité des chambres")],
    ["room type", resolveDomainTarget("fr", "Room Type", "Type de chambre")],
    ["availability", resolveDomainTarget("fr", "Availability", "Disponibilité")],
    ["rate plan", resolveDomainTarget("fr", "Rate Plan", "Plan tarifaire")],
    ["housekeeping", resolveDomainTarget("fr", "Housekeeping", "Housekeeping")],
  ],
};

const originalTextByNode = new WeakMap<Text, string>();
const originalAttrsByElement = new WeakMap<Element, Map<string, string>>();
let currentPhraseMap = new Map<string, string>();
let orderedPhraseEntries: Array<[string, string]> = [];
let currentLocale: SupportedLocale = DEFAULT_LOCALE;

const liveTranslationCache = new Map<SupportedLocale, Map<string, string>>();
const GOOGLE_TRANSLATE_ENDPOINT = "https://translate.googleapis.com/translate_a/single";
const LIVE_TRANSLATE_BATCH_SIZE = 20;
const LIVE_TRANSLATE_MAX_PHRASE_LENGTH = 180; 
let pendingLivePass: number | null = null;
let mutationObserver: MutationObserver | null = null;
let pendingMutationPass: number | null = null;
let latestI18n: I18nInstance | null = null;

function normalizeLocale(value: string | undefined): SupportedLocale {
  const normalized = (value ?? DEFAULT_LOCALE).toLowerCase();
  if (normalized.startsWith("de")) return "de";
  if (normalized.startsWith("pl")) return "pl";
  if (normalized.startsWith("fr")) return "fr";
  return "en";
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function flattenStrings(input: unknown, target: Map<string, string>, path = "") {
  if (typeof input === "string") {
    target.set(path, input);
    return;
  }

  if (Array.isArray(input)) {
    input.forEach((entry, index) => flattenStrings(entry, target, `${path}[${index}]`));
    return;
  }

  if (!isObjectRecord(input)) return;

  Object.entries(input).forEach(([key, value]) => {
    const nextPath = path ? `${path}.${key}` : key;
    flattenStrings(value, target, nextPath);
  });
}

function createPhraseMap(locale: SupportedLocale): Map<string, string> {
  const baseFlat = new Map<string, string>();
  const targetFlat = new Map<string, string>();
  flattenStrings(translationResources[DEFAULT_LOCALE].translation, baseFlat);
  flattenStrings(translationResources[locale].translation, targetFlat);

  const phraseMap = new Map<string, string>();
  baseFlat.forEach((baseValue, key) => {
    const targetValue = targetFlat.get(key);
    if (!targetValue || targetValue === baseValue) return;
    if (!baseValue.trim()) return;
    if (baseValue.length < 2) return;
    if (!phraseMap.has(baseValue)) {
      phraseMap.set(baseValue, targetValue);
    }
  });
  return phraseMap;
}

function createOrderedPhraseEntries(map: Map<string, string>) {
  return [...map.entries()].sort((a, b) => b[0].length - a[0].length);
}

function normalizePhraseForLookup(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function escapeRegex(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function findManualOverride(input: string): string | null {
  const localeOverrides = MANUAL_PHRASE_OVERRIDES[currentLocale];
  if (!localeOverrides) return null;

  const direct = localeOverrides[input];
  if (direct) return direct;

  const normalizedInput = normalizePhraseForLookup(input);
  for (const [source, target] of Object.entries(localeOverrides)) {
    if (normalizePhraseForLookup(source) === normalizedInput) {
      return target;
    }
  }

  return null;
}

function findDynamicOverride(input: string): string | null {
  if (currentLocale !== "de") return null;

  const rules: Array<[RegExp, (...matches: string[]) => string]> = [
    [/^of (\d+) total · (\d+) non-zero$/i, (_, total, nonZero) => `von ${total} insgesamt · ${nonZero} mit Saldo`],
    [/^(\d+) folio\(s\) with outstanding balance$/i, (_, count) => `${count} Folio(s) mit offenem Saldo`],
    [/^(.+) still open$/i, (_, amount) => `${amount} noch offen`],
    [/^(.+) collected$/i, (_, amount) => `${amount} eingezogen`],
    [/^(.+) awaiting payment$/i, (_, amount) => `${amount} zur Zahlung ausstehend`],
    [/^invoice\(s\) voided or corrected$/i, () => "Rechnung(en) storniert oder korrigiert"],
    [/^(.+) total · (\d+) pending$/i, (_, amount, count) => `${amount} insgesamt · ${count} ausstehend`],
    [/^(\d+) fiscalized · (\d+) pending$/i, (_, fiscalized, pending) => `${fiscalized} fiskalisiert · ${pending} ausstehend`],
    [/^(\d+) active · (\d+) overdue$/i, (_, active, overdue) => `${active} aktiv · ${overdue} überfällig`],
    [
      /^(\d+) overdue arrival(s)? from earlier business dates still block Close Day\.$/i,
      (_, count) => `${count} verspätete Anreise(n) aus früheren Geschäftstagen blockiert weiterhin den Tagesabschluss.`,
    ],
    [/^Unpaid folios: (\d+)$/i, (_, count) => `Unbezahlte Folios: ${count}`],
    [/^Departures not checked out: (\d+)$/i, (_, count) => `Nicht ausgecheckte Abreisen: ${count}`],
    [/^Arrivals not checked in: (\d+)$/i, (_, count) => `Nicht eingecheckte Anreisen: ${count}`],
    [/^Critical work orders: (\d+)$/i, (_, count) => `Kritische Arbeitsaufträge: ${count}`],
    [/^Open parking sessions: (\d+)$/i, (_, count) => `Offene Parkvorgänge: ${count}`],
  ];

  for (const [pattern, format] of rules) {
    const match = input.match(pattern);
    if (match) return format(...match);
  }

  return null;
}

function applyDomainTermOverrides(input: string): string {
  const replacements = DOMAIN_TERM_REPLACEMENTS[currentLocale];
  if (!replacements?.length) return input;

  return replacements.reduce((output, [source, target]) => {
    const pattern = new RegExp(`\\b${escapeRegex(source)}\\b`, "gi");
    return output.replace(pattern, target);
  }, input);
}

function hasLocalOverrideForPhrase(input: string): boolean {
  if (findManualOverride(input)) return true;
  return applyDomainTermOverrides(input) !== input;
}

function translatePhrase(input: string): string {
  const manualOverride = findManualOverride(input);
  if (manualOverride) return manualOverride;

  const dynamicOverride = findDynamicOverride(input);
  if (dynamicOverride) return dynamicOverride;

  const runtimeCache = liveTranslationCache.get(currentLocale);
  const runtime = runtimeCache?.get(input);
  if (runtime) return runtime;

  const exact = currentPhraseMap.get(input);
  if (exact) return exact;

  let output = applyDomainTermOverrides(input);
  for (const [source, target] of orderedPhraseEntries) {
    if (!source || source === target) continue;
    if (!output.includes(source)) continue;
    // Translate complete phrases/words only. A raw substring replacement made
    // translations self-match on later observer passes (for example
    // German `All` -> `Alle` -> `Allee` -> `Alleee` ...).
    output = replaceWholePhrase(output, source, target);
  }

  output = applyDomainTermOverrides(output);
  return output;
}

function shouldAutoTranslatePhrase(value: string): boolean {
  const normalized = value.trim();
  if (!normalized) return false;
  if (normalized.length < 2 || normalized.length > LIVE_TRANSLATE_MAX_PHRASE_LENGTH) return false;
  if (/^\d+$/.test(normalized)) return false;

  const words = normalized.split(/\s+/).filter(Boolean);
  if (words.length < LIVE_TRANSLATE_MIN_WORDS) return false;
  if (NAME_LIKE_PATTERN.test(normalized)) return false;

  return true;
}

function getRuntimeCache(locale: SupportedLocale): Map<string, string> {
  let cache = liveTranslationCache.get(locale);
  if (!cache) {
    cache = new Map<string, string>();
    liveTranslationCache.set(locale, cache);
  }
  return cache;
}

async function fetchGoogleTranslation(text: string, locale: SupportedLocale): Promise<string | null> {
  const params = new URLSearchParams({
    client: "gtx",
    sl: "auto",
    tl: locale,
    dt: "t",
    q: text,
  });

  const response = await fetch(`${GOOGLE_TRANSLATE_ENDPOINT}?${params.toString()}`);
  if (!response.ok) return null;

  const payload = (await response.json()) as unknown;
  if (!Array.isArray(payload) || !Array.isArray(payload[0])) return null;

  const chunks = payload[0] as unknown[];
  const translated = chunks
    .map((chunk) => (Array.isArray(chunk) ? chunk[0] : ""))
    .filter((piece): piece is string => typeof piece === "string")
    .join("");

  return translated.trim() ? translated : null;
}

function collectMissingPhrases(locale: SupportedLocale): string[] {
  const cache = getRuntimeCache(locale);
  const unique = new Set<string>();

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    const textNode = current as Text;
    if (!isSkippableNode(textNode)) {
      const original = originalTextByNode.get(textNode) ?? textNode.nodeValue ?? "";
      if (shouldAutoTranslatePhrase(original)) {
        const mapped = currentPhraseMap.get(original);
        if (!mapped && !cache.has(original) && !hasLocalOverrideForPhrase(original)) {
          unique.add(original);
        }
      }
    }
    current = walker.nextNode();
  }

  return [...unique];
}

function collectMissingAttributePhrases(locale: SupportedLocale): string[] {
  const cache = getRuntimeCache(locale);
  const unique = new Set<string>();

  const elements = document.body.querySelectorAll("*");
  elements.forEach((element) => {
    TRANSLATABLE_ATTRIBUTES.forEach((attrName) => {
      const value = element.getAttribute(attrName);
      if (!value || !shouldAutoTranslatePhrase(value)) return;
      const mapped = currentPhraseMap.get(value);
      if (!mapped && !cache.has(value) && !hasLocalOverrideForPhrase(value)) {
        unique.add(value);
      }
    });
  });

  return [...unique];
}

async function runLiveTranslationPass(locale: SupportedLocale) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!document.body || locale === DEFAULT_LOCALE) return;

  const missing = [...collectMissingPhrases(locale), ...collectMissingAttributePhrases(locale)];
  if (!missing.length) return;

  const cache = getRuntimeCache(locale);
  for (let i = 0; i < missing.length; i += LIVE_TRANSLATE_BATCH_SIZE) {
    const batch = missing.slice(i, i + LIVE_TRANSLATE_BATCH_SIZE);
    await Promise.all(
      batch.map(async (phrase) => {
        try {
          const translated = await fetchGoogleTranslation(phrase, locale);
          if (translated && translated !== phrase) {
            cache.set(phrase, translated);
          }
        } catch {
          // Keep silent and continue with local dictionary-based translation.
        }
      }),
    );
  }

  walkAndTranslate(document.body);
}

function isSkippableNode(node: Node): boolean {
  const parent = node.parentElement;
  if (!parent) return true;

  const tag = parent.tagName;
  if (["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "OPTION"].includes(tag)) return true;
  if (parent.closest(SKIP_TRANSLATION_SELECTOR)) return true;
  if (parent.closest("[contenteditable='true']")) return true;
  if (parent.closest("input, textarea, select")) return true;
  return false;
}

function translateTextNode(node: Text) {
  if (isSkippableNode(node)) return;

  const current = node.nodeValue ?? "";
  let original = originalTextByNode.get(node);
  if (original === undefined) {
    original = current;
    originalTextByNode.set(node, original);
  } else {
    const previousTranslation = translatePhrase(original);
    // React commonly reuses the same Text node for dynamic values. When its
    // content changes (nights, prices, balances, counters…), that new content
    // is the new source text. Keeping the first value forever made the
    // translation observer overwrite fresh UI state after every render.
    if (current !== original && current !== previousTranslation) {
      original = current;
      originalTextByNode.set(node, original);
    }
  }

  const replacement = translatePhrase(original);
  if (node.nodeValue !== replacement) {
    node.nodeValue = replacement;
  }
}

function translateElementAttributes(element: Element) {
  if (element.closest(SKIP_TRANSLATION_SELECTOR)) return;

  let originalAttrs = originalAttrsByElement.get(element);
  if (!originalAttrs) {
    originalAttrs = new Map<string, string>();
    originalAttrsByElement.set(element, originalAttrs);
  }

  TRANSLATABLE_ATTRIBUTES.forEach((attrName) => {
    const currentValue = element.getAttribute(attrName);
    if (!currentValue) return;

    const originalValue = originalAttrs?.get(attrName) ?? currentValue;
    if (!originalAttrs?.has(attrName)) {
      originalAttrs?.set(attrName, originalValue);
    }

    const translated = translatePhrase(originalValue);
    if (currentValue !== translated) {
      element.setAttribute(attrName, translated);
    }
  });
}

function walkAndTranslate(root: Node) {
  if (root.nodeType === Node.TEXT_NODE) {
    translateTextNode(root as Text);
    return;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    translateTextNode(current as Text);
    current = walker.nextNode();
  }

  if (root.nodeType === Node.ELEMENT_NODE) {
    translateElementAttributes(root as Element);
  }

  if (root.nodeType === Node.DOCUMENT_FRAGMENT_NODE || root.nodeType === Node.ELEMENT_NODE) {
    const elementWalker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
    let currentElement = elementWalker.nextNode();
    while (currentElement) {
      translateElementAttributes(currentElement as Element);
      currentElement = elementWalker.nextNode();
    }
  }
}

export function translateDocumentOnce() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!document.body) return;
  walkAndTranslate(document.body);
}

export function syncTranslationLanguage(i18n: I18nInstance) {
  const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language);
  currentLocale = locale;
  currentPhraseMap = locale === DEFAULT_LOCALE ? new Map() : createPhraseMap(locale);
  orderedPhraseEntries = createOrderedPhraseEntries(currentPhraseMap);
}

export function prepareDomAutoTranslation(i18n: I18nInstance) {
  latestI18n = i18n;
  syncTranslationLanguage(i18n);
}

export function translateDocumentLive(i18n: I18nInstance) {
  latestI18n = i18n;
  const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language);
  if (pendingLivePass !== null && typeof window !== "undefined") {
    window.clearTimeout(pendingLivePass);
  }
  if (typeof window === "undefined") return;

  pendingLivePass = window.setTimeout(() => {
    void runLiveTranslationPass(locale);
  }, 120);
}

function scheduleMutationDrivenLiveTranslation() {
  if (typeof window === "undefined" || !latestI18n) return;
  if (pendingMutationPass !== null) {
    window.clearTimeout(pendingMutationPass);
  }

  pendingMutationPass = window.setTimeout(() => {
    if (!latestI18n) return;
    translateDocumentOnce();
    translateDocumentLive(latestI18n);
  }, 140);
}

export function enableLiveTranslationObserver(i18n: I18nInstance) {
  latestI18n = i18n;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!document.body || mutationObserver) return;

  mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "characterData") {
        scheduleMutationDrivenLiveTranslation();
        return;
      }
      if (mutation.type === "attributes") {
        scheduleMutationDrivenLiveTranslation();
        return;
      }
      if (mutation.type === "childList" && (mutation.addedNodes.length || mutation.removedNodes.length)) {
        scheduleMutationDrivenLiveTranslation();
        return;
      }
    }
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...TRANSLATABLE_ATTRIBUTES],
  });
}

export function disableLiveTranslationObserver() {
  if (mutationObserver) {
    mutationObserver.disconnect();
    mutationObserver = null;
  }
  if (typeof window !== "undefined" && pendingMutationPass !== null) {
    window.clearTimeout(pendingMutationPass);
    pendingMutationPass = null;
  }
}
